import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

interface Article {
  id: number;
  title: string;
  summary: string;
  content: string;
  image: string | null;
  created_at: string;
  category: string;
  cultivation_stage: string;
  author_name?: string;
  views: number;
  status: string;
}

const Articles: React.FC = () => {
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetch('https://fellfell.ir/api/articles.php', {
      credentials: 'include'
    })
      .then(res => res.json())
      .then(data => {
        if (data.success && Array.isArray(data.data)) {
          setArticles(data.data.filter((a: Article) => a.status === 'published'));
        } else {
          setError('خطا در دریافت مقالات');
        }
        setLoading(false);
      })
      .catch(() => {
        setError('خطا در ارتباط با سرور');
        setLoading(false);
      });
  }, []);

  // ── پاکسازی HTML از متن ──────────────────────────────
  const stripHtml = (html: string): string => {
    if (!html) return '';
    let text = html.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');
    text = text.replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '');
    text = text.replace(/<[^>]+>/g, ' ');
    text = text.replace(/\s+/g, ' ').trim();
    return text;
  };

  const getStageBadge = (stage: string) => {
    const stages: Record<string, { label: string; color: string; bg: string; icon: string }> = {
      'کشت': { label: 'کشت', color: '#2e7d32', bg: '#e8f5e9', icon: '🌱' },
      'داشت': { label: 'داشت', color: '#1565c0', bg: '#e3f2fd', icon: '🌿' },
      'برداشت': { label: 'برداشت', color: '#f57c00', bg: '#fff3e0', icon: '🌾' },
      'نگهداشت': { label: 'نگهداشت', color: '#6a1b9a', bg: '#f3e5f5', icon: '🏺' }
    };
    return stages[stage] || { label: stage, color: '#333', bg: '#f5f5f5', icon: '📌' };
  };

  if (loading) return <div className="loading">⏳ در حال بارگذاری...</div>;
  if (error) return <div className="alert alert-error">{error}</div>;

  return (
    <>
      <div className="page-header" style={{ background: '#1e3a1e', color: '#fff', padding: '40px 0' }}>
        <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
          <h1 style={{ fontSize: '28px' }}>📰 مقالات آموزشی</h1>
          <p style={{ color: '#a5d6a7', marginTop: '5px' }}>جدیدترین مطالب و مقالات تخصصی کشاورزی</p>
        </div>
      </div>
      
      <section style={{ padding: '30px 0' }}>
        <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
          {articles.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', background: '#fff', borderRadius: '16px' }}>
              <div style={{ fontSize: '48px', marginBottom: '15px' }}>📝</div>
              <p style={{ fontSize: '18px', color: '#888' }}>هیچ مقاله‌ای منتشر نشده است</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '25px' }}>
              {articles.map(a => {
                const stage = getStageBadge(a.cultivation_stage || 'کشت');
                return (
                  <div 
                    key={a.id} 
                    style={{ 
                      background: '#fff', 
                      borderRadius: '16px', 
                      padding: '20px', 
                      boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.boxShadow = '0 8px 30px rgba(0,0,0,0.12)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.06)';
                    }}
                  >
                    <div style={{ position: 'relative' }}>
                      <img 
                        src={a.image ? `https://fellfell.ir/uploads/articles/${a.image}` : '/images/default-article.jpg'} 
                        alt={a.title} 
                        style={{ 
                          width: '100%', 
                          height: '180px', 
                          objectFit: 'cover', 
                          borderRadius: '12px', 
                          marginBottom: '15px',
                          background: '#f0f4f0'
                        }}
                        onError={(e) => { (e.target as HTMLImageElement).src = '/images/default-article.jpg'; }}
                        loading="lazy"
                        decoding="async"
                      />
                      {a.category && (
                        <span style={{
                          position: 'absolute',
                          top: '10px',
                          left: '10px',
                          background: '#2e7d32',
                          color: '#fff',
                          padding: '3px 12px',
                          borderRadius: '20px',
                          fontSize: '12px'
                        }}>
                          {a.category}
                        </span>
                      )}
                      <span style={{
                        position: 'absolute',
                        top: '10px',
                        right: '10px',
                        background: stage.bg,
                        color: stage.color,
                        padding: '3px 12px',
                        borderRadius: '20px',
                        fontSize: '12px',
                        fontWeight: '600'
                      }}>
                        {stage.icon} {stage.label}
                      </span>
                    </div>
                    
                    <h3 style={{ margin: '5px 0 10px', fontSize: '18px', color: '#1e3a1e', lineHeight: '1.5' }}>
                      <Link to={`/article/${a.id}`} style={{ color: '#1e3a1e', textDecoration: 'none' }}>
                        {a.title}
                      </Link>
                    </h3>
                    
                    {/* ── نمایش متن ساده بدون HTML ─────────── */}
                    <p style={{ color: '#666', fontSize: '14px', lineHeight: '1.7', flex: 1 }}>
                      {stripHtml(a.summary || a.content || '').substring(0, 120)}...
                    </p>
                    
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '15px', paddingTop: '12px', borderTop: '1px solid #f0f0f0', fontSize: '12px', color: '#888' }}>
                      <span>👤 {a.author_name || 'نویسنده'}</span>
                      <span>📅 {new Date(a.created_at).toLocaleDateString('fa-IR')}</span>
                    </div>
                    
                    <Link 
                      to={`/article/${a.id}`} 
                      style={{ 
                        display: 'inline-block',
                        marginTop: '12px',
                        padding: '8px 20px',
                        background: '#2e7d32',
                        color: '#fff',
                        borderRadius: '8px',
                        textDecoration: 'none',
                        fontSize: '14px',
                        fontWeight: '600',
                        textAlign: 'center'
                      }}
                    >
                      ادامه مطلب →
                    </Link>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default Articles;